import * as React from 'react';
import { cn } from '@/lib/utils';

/* A minimal toast, on a plain external store rather than a state library: the
   only state library in this app is Zustand, and Zustand is reserved for stream
   state (see stores/stream-store.ts). A notification queue is neither server
   state nor stream state. */

export type ToastTone = 'info' | 'ok' | 'error';

export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
}

type Listener = () => void;

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot() {
  return items;
}

export function toast(tone: ToastTone, title: string, body?: string) {
  const id = nextId++;
  items = [...items, { id, tone, title, body }];
  emit();
  setTimeout(() => dismissToast(id), tone === 'error' ? 9000 : 4500);
  return id;
}

export function dismissToast(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

export function ToastViewport() {
  const list = React.useSyncExternalStore(subscribe, snapshot, snapshot);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {list.map((item) => (
        <div
          key={item.id}
          className={cn(
            'pointer-events-auto animate-pop-in rounded-sm border bg-raised px-3 py-2 shadow-pop',
            item.tone === 'error' ? 'border-danger' : item.tone === 'ok' ? 'border-ok' : 'border-rule-strong',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p
                className={cn(
                  'text-sm font-medium',
                  item.tone === 'error' ? 'text-danger' : item.tone === 'ok' ? 'text-ok' : 'text-ink',
                )}
              >
                {item.title}
              </p>
              {item.body ? <p className="mt-0.5 break-words text-xs text-ink-2">{item.body}</p> : null}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(item.id)}
              className="-mr-1 -mt-1 shrink-0 rounded-sm px-1 text-ink-3 hover:text-ink"
              aria-label="Dismiss"
            >
              <svg viewBox="0 0 12 12" className="h-3 w-3 stroke-current" strokeWidth="1.4" fill="none">
                <path d="M2 2l8 8M10 2l-8 8" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
